import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import type { components } from '@/api/schema';
import { prettyJson } from '@/lib/json';
import { isBlocking } from './logic';

type DeployCheckResult = components['schemas']['DeployCheckResult'];

export function DeployChecksList({ result }: { result: DeployCheckResult }) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col divide-y rounded-md border" aria-label={t('admin.deploy.checks')}>
      {result.checks.map((c) => {
        const blocking = isBlocking(c);
        const Icon = c.ok ? CheckCircle2 : blocking ? XCircle : AlertTriangle;
        const color = c.ok ? 'text-success' : blocking ? 'text-destructive' : 'text-warning';
        const details = c.details !== null && c.details !== undefined ? prettyJson(c.details) : null;
        return (
          <li key={c.id} className="flex items-start gap-2 px-3 py-2" data-testid={`check-${c.id}`} data-status={c.ok ? 'pass' : blocking ? 'fail' : 'warn'}>
            <Icon className={`mt-0.5 size-4 shrink-0 ${color}`} aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t(`admin.deploy.check.${c.id}`, { defaultValue: c.id })}</div>
              <div className="text-xs text-muted-foreground">{c.message}</div>
              {details && !c.ok && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-muted-foreground">{t('admin.deploy.details')}</summary>
                  <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">{details}</pre>
                </details>
              )}
            </div>
            <span className="sr-only">{c.ok ? t('admin.deploy.pass') : blocking ? t('admin.deploy.fail') : t('admin.deploy.warn')}</span>
          </li>
        );
      })}
    </ul>
  );
}
